import { Fragment, useState } from 'react';

import { observer } from 'mobx-react-lite';

import { Button } from '@components/button';
import { Checkbox } from '@components/checkbox';
import { FormLabel } from '@components/form-label';
import { IconButton } from '@components/icon-button';
import { IconVisibilityShown } from '@components/icons/visibility-shown';
import { Spinner } from '@components/loading-spinner';
import { Stack } from '@components/stack';
import { TextField } from '@components/text-field';

import { Box } from 'components/Box';
import { IntegrationField } from 'components/IntegrationField';
import { IntegrationHeader } from 'components/IntegrationHeader';
import { RepeatingContainer } from 'components/styles';
import { useStore } from 'models/Provider';

export const RUOnboarding = observer(
  ({ integration, validateItem, validating, remove, preview, renderTriggeredInput }) => {
    const { t } = useStore();
    const [saving, setSaving] = useState(false);

    const [sendWithEsigning, setSendWithEsigning] = useState(true);
    const [registerToUnion, setRegisterToUnion] = useState(false);

    const [includeSecondSignee, setIncludeSecondSignee] = useState(false);
    const [signeeName, setSigneeName] = useState('');
    const [signeeSSN, setSigneeSSN] = useState('');
    const [signeeEmail, setSigneeEmail] = useState('');
    const [signeePhone, setSigneePhone] = useState('');

    async function submit(retrigger, save) {
      const secondSignee = {
        send_contract: sendWithEsigning,
        employee_union: registerToUnion,
        include_second_signee: includeSecondSignee,
        ...(includeSecondSignee && {
          second_signee_name: signeeName,
          second_signee_ssn: signeeSSN,
          second_signee_email: signeeEmail,
          second_signee_phone: signeePhone,
        }),
      };

      setSaving(true);
      if (save || integration.isValid()) {
        await integration.submitForApplication(retrigger, save, secondSignee);
      } else {
        const firstError = integration.firstError();
        if (firstError && firstError.name) {
          const el = document.querySelector(`[data-id=${firstError.name}]`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }
      }
      setSaving(false);
    }

    if (integration.state === 'loading' || integration.refetching) {
      return <Spinner />;
    }

    integration.loadForApplication();

    return (
      <>
        <IntegrationHeader integration={integration} />
        <Box mt="24">
          {saving && <Spinner />}
          {integration.state === 'loaded' && (
            <>
              <Stack space={4}>
                {integration.triggered &&
                  !integration.canRetrigger &&
                  integration.data.map((data, i) =>
                    data.type === 'repeating' ? (
                      <Stack space={1} key={i}>
                        <FormLabel text={data.label} />
                        {data.fields.map((fieldSet, idx) => (
                          <RepeatingContainer key={idx}>
                            {fieldSet.map((fs, fsIdx) => (
                              <div key={fsIdx}>{renderTriggeredInput(fs)}</div>
                            ))}
                          </RepeatingContainer>
                        ))}
                      </Stack>
                    ) : (
                      <Fragment key={i}>{renderTriggeredInput(data)}</Fragment>
                    )
                  )}

                {(!integration.triggered ||
                  (integration.triggered && integration.canRetrigger)) && (
                  <>
                    {integration.data.map((data, i) => (
                      <IntegrationField
                        data={data}
                        refetch={integration.refetchForApplication}
                        validator={validateItem}
                        isDisabled={saving || integration.refetching}
                        key={i}
                      />
                    ))}

                    <Checkbox
                      value={includeSecondSignee}
                      label={t('integrations.ru.onboardingform.add.signee')}
                      onChange={setIncludeSecondSignee}
                    />

                    {includeSecondSignee && (
                      <>
                        <TextField
                          label={t('integrations.ru.onboardingform.signee.name')}
                          onChange={setSigneeName}
                          value={signeeName}
                        />
                        <TextField
                          label={t('integrations.ru.onboardingform.signee.ssn')}
                          onChange={setSigneeSSN}
                          value={signeeSSN}
                        />
                        <TextField
                          label={t('integrations.ru.onboardingform.signee.email')}
                          onChange={setSigneeEmail}
                          value={signeeEmail}
                        />
                        <TextField
                          label={t('integrations.ru.onboardingform.signee.phone')}
                          onChange={setSigneePhone}
                          value={signeePhone}
                          validator={'number'}
                        />
                      </>
                    )}

                    <Checkbox
                      value={sendWithEsigning}
                      label={t('integrations.ru.onboardingform.send.to.esigning')}
                      onChange={setSendWithEsigning}
                    />

                    <Checkbox
                      value={registerToUnion}
                      label={t('integrations.ru.onboardingform.register.to.union')}
                      onChange={setRegisterToUnion}
                    />
                  </>
                )}
              </Stack>

              <Box flex justifyContent="flex-end" mt="20">
                {integration.triggered && integration.canRetrigger && (
                  <Box ml="8">
                    <Button
                      isDisabled={saving}
                      onClick={() => submit(true, false)}
                      text={t('application.onboarding.integration.resend')}
                    />
                  </Box>
                )}
                {integration.canDelete && integration.triggered && (
                  <Box ml="8">
                    <Button
                      variant="primary-red"
                      isDisabled={saving}
                      onClick={remove}
                      text={t('application.onboarding.integration.delete')}
                    />
                  </Box>
                )}
                {!integration.triggered && (
                  <>
                    {integration.canPreview && (
                      <Box ml="8">
                        <IconButton
                          icon={<IconVisibilityShown />}
                          disabled={
                            saving ||
                            integration.refetching ||
                            validating ||
                            integration.hasErrors()
                          }
                          onClick={() => preview()}
                        />
                      </Box>
                    )}
                    <Box ml="8">
                      <Button
                        variant="primary-blue"
                        isDisabled={saving || integration.refetching}
                        onClick={() => submit(false, true)}
                        text={t('application.onboarding.integration.save')}
                      />
                    </Box>
                    <Box ml="8">
                      <Button
                        isDisabled={
                          saving ||
                          integration.refetching ||
                          integration.isBlocked ||
                          validating ||
                          integration.hasErrors()
                        }
                        onClick={() => submit(false, false)}
                        text={t('application.onboarding.integration.submit')}
                      />
                    </Box>
                  </>
                )}
              </Box>
            </>
          )}
        </Box>
      </>
    );
  }
);
